import { useEffect, useMemo, useState, useCallback } from "react";
import AsyncSelect from "react-select/async";
import UsersApi from "../../../../api/business/UsersApi";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    height: 44,
    borderRadius: 12,
    borderColor: state.isFocused ? "var(--bs-primary)" : "var(--bs-border-color)",
    boxShadow: "none",
    fontSize: ".95rem",
    backgroundColor: "var(--bs-body-bg)",
    color: "var(--bs-body-color)",
    "&:hover": {
      borderColor: state.isFocused ? "var(--bs-primary)" : "var(--bs-border-color)",
    },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, height: 44 }),
  dropdownIndicator: (base) => ({ ...base, padding: 8 }),
  clearIndicator: (base) => ({ ...base, padding: 8 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "var(--bs-body-bg)",
    color: "var(--bs-body-color)",
    border: "1px solid var(--bs-border-color)",
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: "var(--bs-body-bg)",
    color: "var(--bs-body-color)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--bs-primary-bg-subtle)"
      : state.isFocused
      ? "var(--bs-tertiary-bg)"
      : "var(--bs-body-bg)",
    color: state.isSelected ? "var(--bs-primary-text-emphasis)" : "var(--bs-body-color)",
    ":active": {
      backgroundColor: "var(--bs-primary-bg-subtle)",
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--bs-body-color)",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--bs-secondary-color)",
  }),
  input: (base) => ({
    ...base,
    color: "var(--bs-body-color)",
  }),
};

const Label = ({ children }) => (
  <div className="text-body-secondary mb-1" style={{ fontSize: ".8rem" }}>
    {children}
  </div>
);

const Pill = ({ children }) => (
  <span className="badge bg-body-tertiary border text-body fw-semibold">{children}</span>
);

export default function TransactionEditForm({
  transaction,
  agentCountry,
  busy = false,
  onEditSubmit,   
  setIsEditing,
}) {
  const [sendAmount, setSendAmount] = useState(String(transaction.sendAmount ?? ""));
  const [receiveAmount, setReceiveAmount] = useState(String(transaction.receiveAmount ?? ""));
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 576px)").matches;
  });

  const baseline = useMemo(
    () => ({
      sendAmount: String(transaction.sendAmount ?? ""),
      receiveAmount: String(transaction.receiveAmount ?? ""),
      agentId: transaction.agentId ? String(transaction.agentId) : "",
      agentLabel: transaction.agentName || "Current agent",
    }),
    [transaction.sendAmount, transaction.receiveAmount, transaction.agentId, transaction.agentName]
  );

  useEffect(() => {
    if (baseline.agentId) {
      setSelectedAgent({ value: baseline.agentId, label: baseline.agentLabel });
    } else {
      setSelectedAgent(null);
    }
  }, [baseline.agentId, baseline.agentLabel]);
  
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia("(max-width: 576px)");
    const handleChange = (event) => setIsMobile(event.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }
    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const loadAgents = useCallback(
    async (inputValue) => {
      if (!agentCountry) return [];
      const page = await UsersApi.listAgents({
        q: inputValue,
        country: agentCountry,
        size: 20,
        sort: "firstName,asc",
        activeOnly: true,
      });
      const list = Array.isArray(page?.content) ? page.content : [];
      return list
        .filter((u) => u?.active !== false)
        .map((u) => ({
          value: String(u.id),
          label: [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || `Agent ${u.id}`,
        }));
    },
    [agentCountry]
  );

  const isDirty = useMemo(() => {
    const curAgentId = selectedAgent?.value ?? "";
    return (
      sendAmount.trim() !== baseline.sendAmount ||
      receiveAmount.trim() !== baseline.receiveAmount ||
      curAgentId !== baseline.agentId
    );
  }, [sendAmount, receiveAmount, selectedAgent, baseline]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isDirty || busy) return;
    onEditSubmit(e);
  };

  return (
    <form onSubmit={handleSubmit} className="pt-1">
      <input type="hidden" name="agentId" value={selectedAgent?.value || ""} />

      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-3">
          <h5 className="mb-0">Edit Transaction</h5>
          <Pill>{transaction.status}</Pill>
        </div>
        {transaction.createdAt && (
          <div className="text-body-secondary small">
            Created: {new Date(transaction.createdAt).toLocaleString()}
          </div>
        )}
      </div>

      <div className="row g-3">
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <Label>Sender</Label>
              <div className="fw-semibold">{transaction.senderName}</div>
              <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                <Pill>{transaction.senderCountry || "-"}</Pill>
                {transaction.senderPhone && <Pill>{transaction.senderPhone}</Pill>}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <Label>Recipient</Label>
              <div className="fw-semibold">{transaction.recipientName}</div>
              <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                <Pill>{transaction.recipientCountry || "-"}</Pill>
                {transaction.recipientPhone && <Pill>{transaction.recipientPhone}</Pill>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-3">
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <Label>Send Amount</Label>
              <div className="input-group">
                <span className="input-group-text rounded-start-3 fw-semibold">
                  {transaction.sendCurrency}
                </span>
                <input
                  name="sendAmount"
                  type="number"
                  step="0.01"
                  className="form-control fw-semibold"
                  value={sendAmount}
                  onChange={(e) => setSendAmount(e.target.value)}
                  disabled={busy}
                />
                <span className="input-group-text rounded-end-3 text-body-secondary">
                  {transaction.sendCurrency}
                </span>
              </div>
              <div className="form-text mt-1">Currency is not editable after creation.</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <Label>Receive Amount</Label>
              <div className="input-group">
                <span className="input-group-text rounded-start-3 fw-semibold">
                  {transaction.receiveCurrency}
                </span>
                <input
                  name="receiveAmount"
                  type="number"
                  step="0.01"
                  className="form-control fw-semibold"
                  value={receiveAmount}
                  onChange={(e) => setReceiveAmount(e.target.value)}
                  disabled={busy}
                />
                <span className="input-group-text rounded-end-3 text-body-secondary">
                  {transaction.receiveCurrency}
                </span>
              </div>
              <div className="form-text mt-1">Currency is not editable after creation.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-3">
        <div className="col-12">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <Label>Assigned Agent</Label>
                {agentCountry && <Pill>{agentCountry}</Pill>}
              </div>

              <AsyncSelect
                classNamePrefix="rs"
                styles={selectStyles}
                cacheOptions
                defaultOptions
                loadOptions={loadAgents}
                value={selectedAgent}
                onChange={(opt) => setSelectedAgent(opt)}
                isClearable
                isDisabled={!agentCountry || busy}
                isSearchable={!isMobile}
                placeholder={agentCountry ? "Search agent by name…" : "Select country first"}
                noOptionsMessage={() =>
                  agentCountry ? "No agents found in this country" : "Select country first"
                }
                aria-label="Assigned agent"
              />
              {!agentCountry && (
                <div className="form-text mt-1">Recipient country is required to list agents.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap justify-content-end gap-2 mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary rounded-3"
          onClick={() => setIsEditing(false)}
          disabled={busy}
        >
          Cancel
        </button>

        <button type="submit" className="btn btn-success rounded-3" disabled={!isDirty || busy}>
          {busy ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}
